import { AlertCircle, Users } from 'lucide-react';
import { useMemo, useState } from 'react';

import { BUTTON_SIZE, BUTTON_VARIANT } from '@/components/ui/types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { LoadingRegion } from '@/components/ui/loading-region';
import { ManagementFrame } from '@/components/ui/management-frame';
import { RESULT_STATE_PLACEMENT, RESULT_STATE_SIZE, RESULT_STATE_TONE } from '@/components/ui/types/result-state-variants-enums';
import { ResultState } from '@/components/ui/result-state';
import { SettingsList } from '@/components/ui/settings-list';
import { Skeleton } from '@/components/ui/skeleton';
import { SORT_DIRECTION } from '@/components/ui/types/sortable-table-header-variants-enums';
import { SortableTableHeader } from '@/components/ui/sortable-table-header';
import { TABLE_CELL_KIND, TABLE_DENSITY } from '@/components/ui/types/table-variants-enums';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { TeamMemberMobileRow, TeamMemberRow } from './team-member-row';

import type { TeamMember } from '../types/team-types';
import { MemberRole } from '@/services/internal-api/accounts/types/accounts-api-service-enums';
import { TEAM_TAB } from '../types/use-team-filters-enums';

enum SORTABLE_COLUMN {
  EMAIL = 'email',
  STATUS = 'status',
  ROLE = 'role',
}



interface TeamMembersTableProps {
  members: TeamMember[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  activeFilterStatus: TEAM_TAB;
  onRoleChange: (memberId: string | number, newRole: TeamMember['role']) => void;
  onResendInvite: (memberId: string | number) => void;
  onCancelInvite: (memberId: string | number) => void;
  onDeleteMember: (memberId: string | number) => void;
  currentUserRole: TeamMember['role'] | null;
  onRetry?: () => void;
  hasActiveSearch?: boolean;
  onClearSearch?: () => void;
}

const LOADING_ROW_KEYS = ['member-loading-1', 'member-loading-2', 'member-loading-3'];

function TeamMembersLoading() {
  return (
    <LoadingRegion aria-label="Team members">
      <ManagementFrame.Mobile data-slot="team-mobile-list">
        <SettingsList className="rounded-none border-0">
          {LOADING_ROW_KEYS.map(rowKey => (
            <li
              key={rowKey}
              className="flex min-h-[4.5rem] items-start gap-3 px-4 py-3"
            >
              <Skeleton aria-hidden="true" className="size-8 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton aria-hidden="true" className="h-4 w-32 max-w-full" />
                <Skeleton aria-hidden="true" className="h-3 w-44 max-w-full" />
                <div className="flex items-center gap-3 pt-1">
                  <Skeleton aria-hidden="true" className="h-3 w-16" />
                  <Skeleton aria-hidden="true" className="h-9 w-[120px]" />
                </div>
              </div>
              <Skeleton aria-hidden="true" className="size-10 shrink-0 rounded-control" />
            </li>
          ))}
        </SettingsList>
      </ManagementFrame.Mobile>

      <ManagementFrame.Desktop data-slot="team-desktop-table">
        <Table className="table-fixed" density={TABLE_DENSITY.COMPACT}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44%]">Member</TableHead>
              <TableHead className="w-[16%]">Status</TableHead>
              <TableHead className="w-[24%]">Role</TableHead>
              <TableHead className="w-16" kind={TABLE_CELL_KIND.ACTION}>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {LOADING_ROW_KEYS.map(rowKey => (
              <TableRow key={rowKey}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Skeleton aria-hidden="true" className="size-8 shrink-0 rounded-full" />
                    <div className="min-w-0 flex-1 space-y-1">
                      <Skeleton aria-hidden="true" className="h-4 w-32 max-w-full" />
                      <Skeleton aria-hidden="true" className="h-3 w-44 max-w-full" />
                    </div>
                  </div>
                </TableCell>
                <TableCell kind={TABLE_CELL_KIND.STATUS}>
                  <Skeleton aria-hidden="true" className="h-3 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton aria-hidden="true" className="h-9 w-[120px]" />
                </TableCell>
                <TableCell kind={TABLE_CELL_KIND.ACTION}>
                  <Skeleton aria-hidden="true" className="ml-auto size-10 rounded-control" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ManagementFrame.Desktop>
    </LoadingRegion>
  );
}

interface TeamMembersResultProps {
  title: string;
  description: string;
  tone: RESULT_STATE_TONE;
  actionLabel?: string;
  onAction?: () => void;
}

function TeamMembersResult({
  title,
  description,
  tone,
  actionLabel,
  onAction,
}: TeamMembersResultProps) {
  return (
    <ManagementFrame.State>
      <ResultState.Root
        placement={RESULT_STATE_PLACEMENT.SECTION}
        size={RESULT_STATE_SIZE.COMPACT}
        role={tone === RESULT_STATE_TONE.ERROR ? 'alert' : undefined}
      >
        <ResultState.Icon tone={tone}>
          {tone === RESULT_STATE_TONE.ERROR ? <AlertCircle /> : <Users />}
        </ResultState.Icon>
        <ResultState.Content>
          <ResultState.Title>{title}</ResultState.Title>
          <ResultState.Description>{description}</ResultState.Description>
          {actionLabel && onAction ? (
            <ResultState.Actions>
              <Button
                type="button"
                size={BUTTON_SIZE.COMPACT}
                variant={BUTTON_VARIANT.SECONDARY}
                onClick={onAction}
              >
                {actionLabel}
              </Button>
            </ResultState.Actions>
          ) : null}
        </ResultState.Content>
      </ResultState.Root>
    </ManagementFrame.State>
  );
}

export function TeamMembersTable({
  members,
  isLoading,
  isError,
  activeFilterStatus,
  onRoleChange,
  onResendInvite,
  onCancelInvite,
  onDeleteMember,
  currentUserRole,
  onRetry,
  hasActiveSearch = false,
  onClearSearch,
}: TeamMembersTableProps) {
  const [sortColumn, setSortColumn] = useState<SORTABLE_COLUMN | null>(null);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTION.ASC | SORT_DIRECTION.DESC>(
    SORT_DIRECTION.ASC
  );

  const handleSort = (column: SORTABLE_COLUMN) => {
    if (sortColumn === column) {
      setSortDirection(prev =>
        prev === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
      );
    } else {
      setSortColumn(column);
      setSortDirection(SORT_DIRECTION.ASC);
    }
  };

  const columnDirection = (column: SORTABLE_COLUMN): SORT_DIRECTION => {
    if (sortColumn !== column) return SORT_DIRECTION.NONE;
    return sortDirection;
  };

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      if (!sortColumn) {
        if (a.isCurrentUser) return -1;
        if (b.isCurrentUser) return 1;
      }
      const aValue = a[sortColumn ?? SORTABLE_COLUMN.EMAIL];
      const bValue = b[sortColumn ?? SORTABLE_COLUMN.EMAIL];
      if (aValue < bValue) return sortDirection === SORT_DIRECTION.ASC ? -1 : 1;
      if (aValue > bValue) return sortDirection === SORT_DIRECTION.ASC ? 1 : -1;
      return 0;
    });
  }, [members, sortColumn, sortDirection]);

  const availableRoles = useMemo(() => {
    const allRoles: MemberRole[] = Object.values(MemberRole);
    return currentUserRole === MemberRole.ADMIN ? allRoles.filter(r => r !== MemberRole.OWNER) : allRoles;
  }, [currentUserRole]);

  if (isLoading) {
    return <TeamMembersLoading />;
  }

  if (isError) {
    return (
      <TeamMembersResult
        title="Unable to load team members"
        description="We couldn't load your team members. Try again."
        tone={RESULT_STATE_TONE.ERROR}
        actionLabel={onRetry ? 'Retry' : undefined}
        onAction={onRetry}
      />
    );
  }

  if (sortedMembers.length === 0) {
    if (hasActiveSearch) {
      return (
        <TeamMembersResult
          title="No members match your search"
          description="Try a different name or email."
          tone={RESULT_STATE_TONE.NEUTRAL}
          actionLabel={onClearSearch ? 'Clear search' : undefined}
          onAction={onClearSearch}
        />
      );
    }

    if (activeFilterStatus === TEAM_TAB.PENDING) {
      return (
        <TeamMembersResult
          title="No pending invitations"
          description="Invitations you send will appear here until they are accepted."
          tone={RESULT_STATE_TONE.NEUTRAL}
        />
      );
    }

    return (
      <TeamMembersResult
        title="No team members yet"
        description="Invite someone to start collaborating with your team."
        tone={RESULT_STATE_TONE.NEUTRAL}
      />
    );
  }

  const rowProps = {
    availableRoles,
    currentUserRole,
    onRoleChange,
    onResendInvite,
    onCancelInvite,
    onDeleteMember,
  };

  return (
    <>
      <ManagementFrame.Mobile data-slot="team-mobile-list">
        <SettingsList className="rounded-none border-0">
          {sortedMembers.map(member => (
            <TeamMemberMobileRow key={member.id} member={member} {...rowProps} />
          ))}
        </SettingsList>
      </ManagementFrame.Mobile>

      <ManagementFrame.Desktop data-slot="team-desktop-table">
        <Table className="table-fixed" density={TABLE_DENSITY.COMPACT}>
          <TableHeader>
            <TableRow>
              <SortableTableHeader
                className="w-[44%]"
                sortDirection={columnDirection(SORTABLE_COLUMN.EMAIL)}
                onSort={() => handleSort(SORTABLE_COLUMN.EMAIL)}
              >
                Member
              </SortableTableHeader>
              <SortableTableHeader
                className="w-[16%]"
                sortDirection={columnDirection(SORTABLE_COLUMN.STATUS)}
                onSort={() => handleSort(SORTABLE_COLUMN.STATUS)}
              >
                Status
              </SortableTableHeader>
              <SortableTableHeader
                className="w-[24%]"
                sortDirection={columnDirection(SORTABLE_COLUMN.ROLE)}
                onSort={() => handleSort(SORTABLE_COLUMN.ROLE)}
              >
                Role
              </SortableTableHeader>
              <TableHead className="w-16" kind={TABLE_CELL_KIND.ACTION}>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedMembers.map(member => (
              <TeamMemberRow key={member.id} member={member} {...rowProps} />
            ))}
          </TableBody>
        </Table>
      </ManagementFrame.Desktop>
    </>
  );
}
